/**
 * iter-170am — Global Account View (cross-spaces aggregated dashboard).
 *
 * Read-only summary of every space the user belongs to (personal +
 * any team space invited to). Surfaces high-level totals + per-space
 * breakdown rows so the user can see "did I miss anything elsewhere?"
 * without changing the active workspace context.
 *
 * Strategy: client of ``GET /api/dashboard/global`` which returns
 * aggregates for all spaces in a single round-trip. Clicking a row
 * acts as a fast-switch (full reload) into that space's regular
 * dashboard.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../../components/Layout";
import { PageHeader } from "../../components/PageHeader";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { BottomSheet } from "../../components/ui/bottom-sheet";
import { z } from "zod";
import { useZodForm, FormField } from "../../components/form-ux";
import {
  Briefcase, Crown, Users, Coins, TrendingDown, TrendingUp, Target,
  Receipt, Wallet, ArrowRight, Loader2, Globe, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../contexts/LangContext";
import { useSpace } from "../../contexts/SpaceContext";
import { tx } from "../../i18n";
import { api } from "../../lib/api";
import { formatApiError, formatMoney } from "../../lib/api";
import { WorkspaceNameEditor } from "../../components/dashboard/WorkspaceNameEditor";

const ROLE_TONE = {
  owner:  "bg-sky-100 text-sky-700",
  editor: "bg-emerald-100 text-emerald-700",
  viewer: "bg-slate-100 text-slate-600",
};

function StatCard({ label, value, Icon, tone }) {
  return (
    <Card className="p-4 rounded-2xl" data-testid={`global-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${tone}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</p>
          <p className="mt-0.5 text-lg font-semibold text-slate-900 tabular-nums">{value}</p>
        </div>
      </div>
    </Card>
  );
}

function SpaceRow({ space, onPick, isActive, lang, refetchSpaces }) {
  const Icon = space.kind === "team" ? Users : Crown;
  const tone = ROLE_TONE[space.my_role] || ROLE_TONE.viewer;
  const cashLines = Object.entries(space.cash_by_currency || {});

  return (
    <Card
      className={`p-4 rounded-2xl transition-all ${isActive ? "border-emerald-300 bg-emerald-50/40" : "hover:border-slate-300"}`}
      data-testid={`global-space-row-${space.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${space.kind === "team" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}> {/* design-system: ALLOW legacy-palette */}
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-slate-900 truncate">{space.name}</h3>
            {/* iter-187 — Owners of team spaces can rename inline from
                the Global view. Personal spaces and non-owners see no
                pencil (backend rejects PATCH on personal anyway). */}
            <WorkspaceNameEditor space={space} compact refetchSpaces={refetchSpaces} />
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${tone}`}>
              {space.my_role}
            </span>
            {isActive && (
              <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">
                {tx(lang, "Active", "Activo", "Ativo")}
              </Badge>
            )}
          </div>

          {/* Cash by currency */}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {cashLines.length === 0 ? (
              <span className="text-[11px] italic text-slate-400">
                {tx(lang, "No cash recorded", "Sin caja registrada", "Sem caixa registrada")}
              </span>
            ) : cashLines.map(([ccy, amount]) => (
              <span key={ccy} className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-700 tabular-nums font-mono">
                {formatMoney(amount, ccy)}
              </span>
            ))}
          </div>

          {/* Quick stats grid */}
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px]">
            <StatChip Icon={Receipt} tone="text-emerald-700" label={tx(lang, "Receivables", "Cobros", "Cobranças")}
              count={space.receivables.count} />
            <StatChip Icon={Wallet} tone="text-rose-700" label={tx(lang, "Payments", "Pagos", "Pagamentos")}
              count={space.payments.count} />
            <StatChip Icon={TrendingUp} tone="text-sky-700" label={tx(lang, "Investments", "Inversiones", "Investimentos")}
              count={space.investments.count} />
            <StatChip Icon={TrendingDown} tone="text-amber-700" label={tx(lang, "Debts", "Deudas", "Dívidas")}
              count={space.debts.count} />
            <StatChip Icon={Target} tone="text-sky-700" label={tx(lang, "Goals", "Metas", "Metas")}
              count={space.goals.count} />
          </div>
        </div>
        <Button
          size="sm"
          variant={isActive ? "outline" : "default"}
          onClick={() => onPick(space.id)}
          disabled={isActive}
          data-testid={`global-space-switch-${space.id}`}
          className="gap-1.5 shrink-0 self-start"
        >
          {tx(lang, "Open", "Abrir", "Abrir")}
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </Card>
  );
}

function StatChip({ Icon, tone, label, count }) {
  return (
    <span className="flex items-center gap-1 px-2 py-1 rounded bg-slate-50 border border-slate-100">
      <Icon className={`h-3 w-3 ${tone}`} />
      <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
      <span className="ml-auto font-semibold text-slate-700 tabular-nums">{count}</span>
    </span>
  );
}

export default function GlobalDashboard() {
  const { lang } = useLang();
  const { switchSpace, activeSpaceId, refetch: refetchSpaces } = useSpace();
  const qc = useQueryClient();
  const [openSheet, setOpenSheet] = useState(false);

  const Schema = z.object({
    name: z.string().trim().min(1,
      tx(lang, "Name is required.", "El nombre es obligatorio.", "O nome é obrigatório.")
    ).max(80,
      tx(lang, "Up to 80 characters.", "Hasta 80 caracteres.", "Até 80 caracteres.")
    ),
  });
  const form = useZodForm(Schema, { name: "" }, { formId: "global-create-space" });

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-global"],
    queryFn: async () => (await api.get("/dashboard/global")).data,
    retry: false,
  });
  const spaces = data?.spaces || [];
  const totals = data?.totals || {};

  // iter-170am-part6 — Surface the spaces resource cap as an inline
  // "X of Y used" indicator so the user knows BEFORE hitting "Create"
  // whether they have headroom or need to upgrade. Closes the loop
  // visually instead of forcing a 403 round-trip.
  const { data: myPlan } = useQuery({
    queryKey: ["billing-me-spaces"],
    queryFn: async () => (await api.get("/billing/me")).data,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const spacesCap = (myPlan?.entitlements?.resources || []).find((r) => r?.key === "spaces");
  const capLabel = (() => {
    if (!spacesCap) return null;
    if (spacesCap.unlimited) {
      return tx(lang, "Unlimited spaces", "Espacios ilimitados", "Espaços ilimitados");
    }
    const used = spacesCap.used ?? 0;
    const limit = spacesCap.limit ?? 0;
    return tx(lang, `${used} of ${limit} used`, `${used} de ${limit} usados`, `${used} de ${limit} usados`);
  })();
  const capExhausted = Boolean(spacesCap && !spacesCap.unlimited && (spacesCap.remaining ?? 0) <= 0);

  const createSpace = useMutation({
    mutationFn: async (name) => (await api.post("/spaces", { name })).data,
    onSuccess: (res) => {
      toast.success(tx(lang,
        `Space "${res.name}" created`,
        `Espacio "${res.name}" creado`,
        `Espaço "${res.name}" criado`));
      setOpenSheet(false);
      form.reset({ name: "" });
      qc.invalidateQueries({ queryKey: ["dashboard-global"] });
      qc.invalidateQueries({ queryKey: ["spaces"] });
      refetchSpaces();
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.startsWith("cap_exceeded:spaces")) {
        toast.error(tx(lang,
          "You've reached your team-spaces limit. Upgrade your plan to create more.",
          "Alcanzaste el límite de espacios de equipo. Mejora tu plan para crear más.",
          "Você atingiu o limite de espaços de equipe. Atualize seu plano para criar mais."));
      } else if (detail === "feature_required:roles_permissions" || (typeof detail === "string" && detail.startsWith("feature_required"))) {
        toast.error(tx(lang,
          "Your plan doesn't include team spaces. Upgrade in Plans & packs.",
          "Tu plan no incluye espacios de equipo. Mejóralo en Planes y packs.",
          "Seu plano não inclui espaços de equipe. Atualize em Planos e packs."));
      } else {
        toast.error(formatApiError(detail));
      }
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    await createSpace.mutateAsync(values.name.trim());
  });

  return (
    <Layout>
      <div className="space-y-6 max-w-6xl" data-testid="global-dashboard-page">
        <PageHeader
          title={
            <span className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-sky-600" />
              {tx(lang, "Global view", "Vista global", "Vista global")}
            </span>
          }
          subtitle={tx(lang,
            "Aggregated summary across every space you belong to. Switch in to drill down.",
            "Resumen agregado de todos los espacios a los que perteneces. Entra a uno para profundizar.",
            "Resumo agregado de todos os espaços a que você pertence. Entre em um para detalhar.")}
          actions={
            <div className="flex items-center gap-3 flex-wrap">
              {capLabel && (
                <span
                  data-testid="global-cap-indicator"
                  className={`text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full ${
                    capExhausted
                      ? "bg-rose-100 text-rose-700 border border-rose-200"
                      : "bg-slate-100 text-slate-600 border border-slate-200"
                  }`}
                >
                  {capLabel}
                  {capExhausted && (
                    <span className="ml-1.5 italic font-normal">
                      {tx(lang, "— upgrade plan for more", "— actualiza plan para más", "— atualize plano para mais")}
                    </span>
                  )}
                </span>
              )}
              <Button
                onClick={() => setOpenSheet(true)}
                disabled={capExhausted}
                data-testid="global-create-space-btn"
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                {tx(lang, "New space", "Nuevo espacio", "Novo espaço")}
              </Button>
            </div>
          }
        />

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="global-loading">
            <Loader2 className="h-4 w-4 animate-spin" />
            {tx(lang, "Computing aggregates…", "Calculando agregados…", "Calculando agregados…")}
          </div>
        )}

        {!isLoading && (
          <>
            {/* Top-line totals — 6 KPIs en grid 2 filas × 3 columnas. */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="global-totals-grid">
              <StatCard label={tx(lang, "Spaces", "Espacios", "Espaços")}
                value={totals.spaces_count || 0} Icon={Briefcase} tone="bg-sky-100 text-sky-700" />
              <StatCard label={tx(lang, "Cash", "Caja", "Caixa")}
                value={(totals.cash || 0).toLocaleString()} Icon={Coins} tone="bg-emerald-100 text-emerald-700" />
              <StatCard label={tx(lang, "Receivables", "Cobros", "Cobranças")}
                value={(totals.receivables_pending || 0).toLocaleString()} Icon={Receipt} tone="bg-sky-100 text-sky-700" />
              <StatCard label={tx(lang, "Payments", "Pagos", "Pagamentos")}
                value={(totals.payments_pending || 0).toLocaleString()} Icon={Wallet} tone="bg-rose-100 text-rose-700" />
              <StatCard label={tx(lang, "Investments", "Inversiones", "Investimentos")}
                value={(totals.investments_value || 0).toLocaleString()} Icon={TrendingUp} tone="bg-sky-100 text-sky-700" />
              <StatCard label={tx(lang, "Debts", "Deudas", "Dívidas")}
                value={(totals.debts_balance || 0).toLocaleString()} Icon={TrendingDown} tone="bg-amber-100 text-amber-700" />
            </div>

            {/* Per-space breakdown */}
            <div className="space-y-3" data-testid="global-spaces-list">
              {spaces.length === 0 ? (
                <Card className="p-6 rounded-2xl text-center text-sm text-slate-500" data-testid="global-spaces-empty">
                  {tx(lang, "You don't have any spaces yet.", "Aún no tienes espacios.", "Você ainda não tem espaços.")}
                </Card>
              ) : (
                spaces.map((s) => (
                  <SpaceRow
                    key={s.id}
                    space={s}
                    isActive={s.id === activeSpaceId}
                    onPick={(id) => switchSpace(id)}
                    lang={lang}
                    refetchSpaces={refetchSpaces}
                  />
                ))
              )}
            </div>

            <p className="text-[11px] text-slate-400 italic" data-testid="global-disclaimer">
              {tx(lang,
                "Currency-mixed totals are shown in their native units; the SPA does not auto-FX between spaces.",
                "Los totales en varias monedas se muestran en sus unidades nativas; el SPA no convierte FX entre espacios.",
                "Os totais multi-moeda são exibidos em suas unidades nativas; o SPA não converte FX entre espaços.")}
            </p>
          </>
        )}
      </div>

      {/* iter-170am — Responsive sheet para crear team space: bottomsheet
          en mobile (< md), dialog centrado en desktop. Usa el componente
          ``BottomSheet`` que ya implementa este switch en un solo lugar. */}
      <BottomSheet
        open={openSheet}
        onOpenChange={setOpenSheet}
        size="md"
        testid="global-create-space-sheet"
        title={
          <span className="flex items-center gap-2">
            <Users className="h-4 w-4 text-sky-600" />
            {tx(lang, "Create team space", "Crear espacio de equipo", "Criar espaço de equipe")}
          </span>
        }
        description={tx(lang,
          "A team space is a separate dataset where you can invite members. Each space has its own dashboard, payments, receivables, investments, debts and goals. Integrations stay shared across spaces.",
          "Un espacio de equipo es un dataset separado al que puedes invitar miembros. Cada espacio tiene su propio dashboard, pagos, cobros, inversiones, deudas y metas. Las integraciones son transversales.",
          "Um espaço de equipe é um dataset separado ao qual você pode convidar membros. Cada espaço tem seu próprio dashboard, pagamentos, cobranças, investimentos, dívidas e metas. As integrações são transversais.")}
        footer={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setOpenSheet(false)}
              data-testid="global-create-space-cancel"
              className="flex-1"
            >
              {tx(lang, "Cancel", "Cancelar", "Cancelar")}
            </Button>
            <Button
              onClick={onSubmit}
              disabled={!form.values.name.trim() || createSpace.isPending}
              data-testid="global-create-space-submit"
              className="flex-1 gap-2"
            >
              {createSpace.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {tx(lang, "Create space", "Crear espacio", "Criar espaço")}
            </Button>
          </div>
        }
      >
        <form onSubmit={onSubmit} className="space-y-3" noValidate>
          <FormField
            label={tx(lang, "Space name", "Nombre del espacio", "Nome do espaço")}
            name="name"
            required
            hint={tx(lang,
              "Up to 80 characters. You can rename it later.",
              "Hasta 80 caracteres. Puedes renombrarlo después.",
              "Até 80 caracteres. Você pode renomear depois.")}
            error={form.errors.name && (form.touched.name || form.submitCount > 0) ? form.errors.name : undefined}
          >
            <FormField.Input
              placeholder={tx(lang, "e.g. Cliente Acme", "ej. Cliente Acme", "ex. Cliente Acme")}
              maxLength={80}
              autoFocus
              data-testid="global-create-space-input"
              className="h-10"
              {...form.getFieldProps("name")}
            />
          </FormField>
        </form>
      </BottomSheet>
    </Layout>
  );
}
